"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleHelp as HelpCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqData: FaqItem[] = [{
  id: "faq-1",
  question: "What does an ecommerce SEO consultant do?",
  answer: "An ecommerce SEO consultant audits your online store's technical health, researches how your customers search for products, optimises product and category pages for those queries, and develops an ongoing search strategy focused on commercial growth. The role spans technical fixes, content, site architecture and performance measurement."
}, {
  id: "faq-2",
  question: "How is ecommerce SEO different from general SEO?",
  answer: "Ecommerce SEO deals with large product catalogues, product variants (sizes, colours, packs), faceted navigation, stock changes and transactional search intent. General SEO for a service business might involve ten pages; an ecommerce site can have thousands of URLs that each need crawl, index and content decisions."
}, {
  id: "faq-3",
  question: "Can ecommerce SEO increase online store sales?",
  answer: "SEO can increase qualified traffic and purchase opportunities, and a strong ecommerce SEO strategy can reduce reliance on paid activity. However, SEO does not guarantee specific sales figures; conversion depends on pricing, product quality, user experience and other factors."
}, {
  id: "faq-4",
  question: "What ecommerce SEO services do you provide?",
  answer: "I provide ecommerce SEO audits, keyword research, product page optimisation, category page SEO, technical SEO, site architecture planning, structured data recommendations, content strategy, competitor analysis, authority building and ongoing measurement. These can be combined as a one-off project or continuous consulting."
}, {
  id: "faq-5",
  question: "How do you optimise product pages?",
  answer: "I research relevant keywords, rewrite or brief unique product descriptions, optimise titles and metadata, improve imagery and alt text, recommend product schema, set up internal links to related products and categories, and review mobile usability. Changes are reviewed against search performance data."
}, {
  id: "faq-6",
  question: "Why are category pages important for ecommerce SEO?",
  answer: "Category pages serve as landing pages for broad commercial searches such as \"running shoes UK\" or \"oak dining tables\". They can capture more organic traffic and revenue than individual product pages because they match the way shoppers search when they have not decided on a specific item."
}, {
  id: "faq-7",
  question: "Can you help with Shopify SEO?",
  answer: "Yes. I provide ecommerce SEO support for Shopify stores, as I do across other platforms. Typical work includes collection page optimisation, handling tag-based filter duplication, theme-level technical fixes, app selection and structured data configuration."
}, {
  id: "faq-8",
  question: "Can you help with WooCommerce SEO?",
  answer: "Yes. I work with WooCommerce stores on permalink structure, category taxonomy decisions, plugin hygiene, performance optimisation and crawlable parameter management."
}, {
  id: "faq-9",
  question: "How do you handle duplicate product pages?",
  answer: "Depending on the scenario, I use canonical tags to consolidate signals, redirect duplicate URLs to the correct canonical page, revise site architecture to remove unnecessary pages, or merge thin variants into a single stronger page."
}, {
  id: "faq-10",
  question: "How long does ecommerce SEO take?",
  answer: "SEO improvements can take weeks or months to show results because of search engine processing, competition and the scale of changes needed. Technical fixes and category visibility can move earlier than organic revenue, which generally needs sustained, consistent work. I do not promise a fixed timeline or specific results."
}, {
  id: "faq-11",
  question: "How do you measure ecommerce SEO success?",
  answer: "I track organic revenue, conversion rates on key landing pages, commercial keyword visibility, Search Console impressions and clicks, assisted conversions and technical health metrics. The focus is on revenue and qualified traffic, not vanity metrics like total pageviews."
}, {
  id: "faq-12",
  question: "How much does ecommerce SEO consultancy cost?",
  answer: "Pricing depends on your store's size, platform complexity and scope of work. Typical engagement models include one-off audits, project-based work (such as migration support or a full keyword mapping exercise) and ongoing monthly consulting. I discuss scope and budget during the initial consultation."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
  return <section id="faqecommerceseo" data-nav="light" className="bg-muted/40 text-foreground relative py-20 lg:py-28 overflow-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary bg-secondary border-none">
              <ShieldCheck className="w-4 h-4 text-primary" />
              Clear Answers on Ecommerce Search
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Frequently asked questions about ecommerce SEO
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-default">
            Common questions about technical architecture, catalogue optimisation, platform capabilities and realistic expectations for online retail search growth.
          </p>
        </motion.div>

        {/* Content Split */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Side Card */}
          <motion.div variants={itemVariants} className="lg:col-span-4 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm relative overflow-hidden">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                <HelpCircle className="w-6 h-6" />
              </div>

              <h3 className="text-xl md:text-2xl font-semibold text-card-foreground font-default mb-3">
                Have a question about your store?
              </h3>

              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                Every online store has unique catalogue constraints and commercial goals. I can review your specific setup and explain where organic search fits into your growth plans.
              </p>

              <div className="space-y-3 pt-2 border-t border-border">
                <Button asChild className="w-full justify-center gap-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium py-2.5 shadow-sm">
                  <Link to="/ContactPage">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="w-full justify-center gap-2 rounded-md border-border text-foreground hover:bg-muted text-sm font-medium py-2.5">
                  <Link to="/ContactPage?topic=free-seo-audit">
                    <span>Get a Free SEO Audit</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: FAQ Accordion */}
          <motion.div variants={itemVariants} className="lg:col-span-8">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-sm">
              <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-4">
                {faqData.map((item, i) => <AccordionItem data-index={i} key={item.id} value={item.id} className="border border-border/80 rounded-lg px-4 md:px-5 transition-colors data-[state=open]:bg-muted/30 data-[state=open]:border-primary/30">
                    <AccordionTrigger className="py-4 hover:no-underline text-base md:text-lg font-semibold text-foreground flex items-start text-left gap-3">
                      <span className="flex-1 font-default leading-snug">
                        {item.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5 pt-1 font-default">
                      <p>{item.answer}</p>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}